import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useDeleteUserMutation,
  useLazyViewCurrentConsumerEmailQuery,
  useUpdateEmailMutation,
} from "../../../../../redux/api/user/userAPI";
import {
  setPrivileges,
  setToken,
} from "../../../../../redux/features/auth/auth-slice";
import { resetProfile } from "../../../../../redux/features/user/user-slice";
import { AppRoute } from "../../../interfaces/app-routes";
import AppButton from "../../../ui-elements/buttons/app-button/app-button";
import AppInput from "../../../ui-elements/input/app-input";
import SpinnerModal from "../../../ui-elements/spinner/spinner";
import { emailRegex } from "../../../utils/regex";
import { showErrorMessage, showSuccessMessage } from "../../../utils/toast";
import { IChangeEmailFormInput } from "../../change-email/change-email-form/change-email-form";
import UserAccountCancelModal from "../user-account-cancel-modal/user-account-cancel-modal";

const defaultFormValues: IChangeEmailFormInput = {
  email: "",
};

const EmailVerificationForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deleteUser] = useDeleteUserMutation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<IChangeEmailFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  const [
    triggerViewCurrentConsumerEmail,
    { isFetching: isFetchingViewCurrentConsumerEmail },
  ] = useLazyViewCurrentConsumerEmailQuery();
  const [updateEmail, { isLoading: isLoadingUpdateEmail }] =
    useUpdateEmailMutation();
  const [userAccountCancelModal, setUserAccountCancelModal] = useState(false);

  useEffect(() => {
    triggerViewCurrentConsumerEmail()
      .unwrap()
      .then((response) => {
        setValue("email", response.email);
      })
      .catch((error) => {
        showErrorMessage(error.message || "Something went wrong");
      });
  }, [triggerViewCurrentConsumerEmail, setValue]);

  const onSubmit = (data: IChangeEmailFormInput) => {
    updateEmail({ email: data.email })
      .unwrap()
      .then(() => {
        navigate(AppRoute.VERIFY_EMAIL_OTP, {
          state: {
            email: data.email,
            whereTo: "dashboard/user-profile",
          },
        });
        reset({ ...defaultFormValues });
      })
      .catch((error) => {
        showErrorMessage(error.message || "Something went wrong");
      });
  };

  const onCancelAccountCreation = () => {
    deleteUser()
      .unwrap()
      .then(() => {
        showSuccessMessage("Account Creation Canceled Successfully");
        const country = localStorage.getItem("country");
        localStorage.clear();
        dispatch(setToken(""));
        dispatch(resetProfile());
        dispatch(setPrivileges([]));

        if (country !== null) {
          localStorage.setItem("country", country);
        }
      })
      .catch((error) => {
        showErrorMessage(error.message || "Failed to delete user");
      });
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col>
            <AppInput
              name="email"
              placeholder="Email"
              label="New Email"
              register={register("email", {
                required: "Email is required",
                pattern: {
                  value: emailRegex,
                  message: "Email is invalid",
                },
              })}
              errors={errors}
            />
          </Col>
        </Row>
        <Row className="mt-5">
          <Col>
            <AppButton
              text={`Send OTP`}
              size="medium"
              type="submit"
              isLoading={isLoadingUpdateEmail}
            />
          </Col>
        </Row>
        <Row className="justify-content-center mt-4 ">
          <Col
            className="text-decoration-underline cursor-pointer text-center text-color-mode"
            onClick={() => setUserAccountCancelModal(true)}
          >
            Cancel Account Creation
          </Col>
        </Row>
      </form>
      <UserAccountCancelModal
        show={userAccountCancelModal}
        onCancel={() => setUserAccountCancelModal(false)}
        onClose={() => setUserAccountCancelModal(false)}
        onConfirm={() => {
          setUserAccountCancelModal(false);
          onCancelAccountCreation();
        }}
      />
      <SpinnerModal show={isFetchingViewCurrentConsumerEmail} />
    </div>
  );
};

export default EmailVerificationForm;
